import { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import Constant from '@/common/constant/Constant';
import CorporationUtils from '@/common/utils/CorporationUtils';
import Utils from '@/common/utils/utils';
import { corporationAtom } from '@/state/atom/corporationAtom';
import { projectInuseDetailAtom } from '@/state/atom/projectAtom';
import { userAtom } from '@/state/atom/userAtom';
import {
    contentDetailOnDemandToggleOnSelector,
    listOnDemandToggleOnSelector
} from '@/state/selector/translationSelector';
import {
    findTranslationAttachFile,
    getUserMainLanguageCode,
    toTranslatedDisplayFile
} from '@/common/utils/translatedAttachmentUtils';

export const ATTACHMENT_DISPLAY_CONTEXT = {
    LIST: 'list',
    DETAIL: 'detail'
};

/**
 * Decides, per attachment, whether the translation in the viewer's main language is shown in place
 * of the original (CR-303134 BR-1). All of these must hold:
 *   - the「翻訳版」toggle is ON: the list toggle of `viewKey` in the LIST context (list / modal
 *     opened from a list), the content's own toggle in the DETAIL context; never in edit mode
 *   - file translation is enabled for the viewer (same gate as the 翻訳 menu option)
 *   - the viewer has a main language, and a finished translation in exactly that language exists
 *
 * The context is explicit: a list whose view key is not known yet reads as OFF, it never falls
 * back to the detail toggle of the same ticket.
 *
 * @param {Object} params
 * @param {Array|string} params.attachFiles AttachFile records of the ticket (array or JSON string)
 * @param {string} params.context           ATTACHMENT_DISPLAY_CONTEXT.LIST | DETAIL
 * @param {string} [params.panelType]       LIST: list panel type
 * @param {string} [params.viewKey]         LIST: list view key
 * @param {string} [params.contentId]       DETAIL: ticket id (child email id for an email)
 * @param {string} [params.displayMode]     DETAIL: Constant.DISPLAY_MODE of the screen
 * @returns {{ getTranslation: Function, getDisplayFile: Function }}
 *   getTranslation(file) -> translation AttachFile record or null
 *   getDisplayFile(file) -> file to render/open (the translation when shown, else the original)
 */
const useTranslatedAttachmentDisplay = ({ attachFiles, context, panelType, viewKey, contentId, displayMode } = {}) => {
    const corporationDetail = useRecoilValue(corporationAtom);
    const projectInuseDetail = useRecoilValue(projectInuseDetailAtom);
    const userDetail = useRecoilValue(userAtom);

    const isListContext = context === ATTACHMENT_DISPLAY_CONTEXT.LIST;
    const isDetailContext = context === ATTACHMENT_DISPLAY_CONTEXT.DETAIL;

    // Both selectors are always read (hooks order); the one of the other context gets empty params.
    const isListToggleOn = useRecoilValue(
        listOnDemandToggleOnSelector({ panelType: isListContext ? panelType || '' : '', viewKey: isListContext ? viewKey || '' : '' })
    );
    const isDetailToggleOn = useRecoilValue(contentDetailOnDemandToggleOnSelector(isDetailContext ? contentId || '' : ''));

    const isToggleOn = isListContext
        ? isListToggleOn
        : isDetailContext && displayMode !== Constant.DISPLAY_MODE.EDIT && isDetailToggleOn;

    const isFileTranslationEnabled = useMemo(() => {
        if (!corporationDetail) return false;
        return Boolean(CorporationUtils.isEnableSettingFileTranslationAttachment(corporationDetail, projectInuseDetail || {}));
    }, [corporationDetail, projectInuseDetail]);

    const mainLanguage = useMemo(() => getUserMainLanguageCode(userDetail), [userDetail]);

    const attachFileList = useMemo(() => {
        const parsed = Utils.convertJSONDataToObject(attachFiles);
        return Array.isArray(parsed) ? parsed : [];
    }, [attachFiles]);

    const getTranslation = useCallback(
        file => {
            if (!isToggleOn || !isFileTranslationEnabled || !mainLanguage || !file?.fileUrl) return null;
            return findTranslationAttachFile(attachFileList, file.fileUrl, mainLanguage);
        },
        [attachFileList, isToggleOn, isFileTranslationEnabled, mainLanguage]
    );

    const getDisplayFile = useCallback(file => toTranslatedDisplayFile(file, getTranslation(file)), [getTranslation]);

    return { getTranslation, getDisplayFile };
};

export default useTranslatedAttachmentDisplay;
